import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Barcode, Plus, Trash2, History } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function ProductInventoryPanel({ productId, canManage }) {
  const { t, fmtNumber } = useI18n();
  const qc = useQueryClient();
  const [code, setCode] = useState("");

  const { data: aliases } = useQuery({ queryKey: ["aliases", productId], queryFn: async () => (await api.get(`/scan/products/${productId}/aliases`)).data });
  const { data: balances } = useQuery({ queryKey: ["balances", productId], queryFn: async () => (await api.get(`/inventory/balances`, { params: { page_size: 200 } })).data.items.filter((b) => b.product_id === productId) });
  const { data: movements } = useQuery({ queryKey: ["movements", productId], queryFn: async () => (await api.get(`/inventory/movements`, { params: { product_id: productId, limit: 20 } })).data });

  const addAlias = async () => {
    if (!code.trim()) return;
    try { await api.post(`/scan/products/${productId}/aliases`, { code }); setCode(""); qc.invalidateQueries({ queryKey: ["aliases", productId] }); }
    catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const del = async (id) => { await api.delete(`/scan/products/${productId}/aliases/${id}`); qc.invalidateQueries({ queryKey: ["aliases", productId] }); };

  return (
    <Card className="p-6" data-testid="product-inventory-panel">
      <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500"><Barcode className="h-4 w-4" />{t("scan.aliases")}</h3>
      <div className="flex flex-wrap gap-2" data-testid="product-aliases">
        {(aliases?.system || []).map((a, i) => (
          <span key={`s${i}`} className="rounded-full bg-slate-100 px-3 py-1 text-xs tabular-nums dark:bg-slate-800">{a.code} <em className="text-slate-400">· {t("scan.system_code")}</em></span>
        ))}
        {(aliases?.aliases || []).map((a) => (
          <span key={a.id} className="flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1 text-xs tabular-nums text-white dark:bg-white dark:text-slate-900" data-testid="product-alias">
            {a.code}
            {canManage && <button type="button" onClick={() => del(a.id)} data-testid="alias-delete"><Trash2 className="h-3 w-3" /></button>}
          </span>
        ))}
      </div>
      {canManage && (
        <div className="mt-3 flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("scan.barcode_placeholder")} className="h-9 max-w-xs" data-testid="alias-input"
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }} />
          <Button type="button" size="sm" variant="outline" onClick={addAlias} data-testid="alias-add"><Plus className="h-4 w-4" />{t("scan.add_barcode")}</Button>
        </div>
      )}

      <h3 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">{t("scan.balances")}</h3>
      <div className="space-y-1 text-sm" data-testid="product-balances">
        {(balances || []).length === 0 ? <p className="text-slate-400">—</p> : balances.map((b) => (
          <div key={b.id} className="flex justify-between rounded bg-slate-50 px-3 py-1.5 dark:bg-slate-800/50">
            <span>{b.warehouse_code}</span>
            <span className="tabular-nums">{t("scan.on_hand")} {fmtNumber(b.quantity, 4)} · {t("scan.reserved")} {fmtNumber(b.reserved, 4)} · {t("scan.available")} {fmtNumber(b.available, 4)}</span>
          </div>
        ))}
      </div>

      <h3 className="mb-2 mt-6 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500"><History className="h-4 w-4" />{t("scan.movements")}</h3>
      {(movements || []).length === 0 ? <p className="text-sm text-slate-400">{t("scan.no_movements")}</p> : (
        <div className="space-y-1 text-xs" data-testid="product-movements">
          {movements.map((m) => (
            <div key={m.id} className="flex justify-between border-b border-slate-100 py-1 dark:border-slate-800">
              <span className="text-slate-500">{new Date(m.created_at).toLocaleString()} · {m.movement_type}{m.reason ? ` · ${m.reason}` : ""}</span>
              <span className={`tabular-nums font-medium ${Number(m.quantity) < 0 ? "text-red-600" : "text-emerald-600"}`}>{Number(m.quantity) > 0 ? "+" : ""}{fmtNumber(m.quantity, 4)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
